/**
 * StudentContext
 *
 * Tracks WHICH student a consultant (or staff) is currently viewing/editing.
 * When a consultant selects "Rahul" from their student list, they call
 * setCurrentStudent(rahul). All downstream components (universities kanban,
 * documents, deadlines) then read from:
 *   consultants/{consultantId}/students/{studentId}/...
 * instead of the legacy users/{userId}/... path.
 *
 * consultantId is injected by the route wrapper:
 * - consultant: own uid
 * - consultant staff: linked consultantId from roles/{uid}
 * so child hooks always hit the correct consultant account without prop-drilling.
 */

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { StudentProfile } from '@/types/roles';

interface StudentContextValue {
  /** The student currently being viewed / edited. Null when none selected. */
  currentStudent: StudentProfile | null;
  /** Call when navigating into a student detail page. */
  setCurrentStudent: (student: StudentProfile | null) => void;
  /** Consultant account id under /consultants/{consultantId}. */
  consultantId: string;
  /** Clear the active student (e.g. on navigating back to the list). */
  clearCurrentStudent: () => void;
}

const StudentContext = createContext<StudentContextValue | undefined>(undefined);

interface StudentProviderProps {
  children: ReactNode;
  /** Pass the resolved consultant account id here. */
  consultantId: string;
}

export function StudentProvider({ children, consultantId }: StudentProviderProps) {
  const [currentStudent, setCurrentStudentState] = useState<StudentProfile | null>(null);

  const setCurrentStudent = useCallback((student: StudentProfile | null) => {
    setCurrentStudentState(student);
  }, []);

  const clearCurrentStudent = useCallback(() => {
    setCurrentStudentState(null);
  }, []);

  return (
    <StudentContext.Provider
      value={{ currentStudent, setCurrentStudent, consultantId, clearCurrentStudent }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export function useStudentContext(): StudentContextValue {
  const ctx = useContext(StudentContext);
  if (!ctx) {
    throw new Error('useStudentContext must be used within a <StudentProvider>');
  }
  return ctx;
}
